import { ChangeDetectionStrategy, Component, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import { connectState } from '@examdojo/angular/util';
import { getAnimationAppearDisappearWithHeight } from '@examdojo/animation';
import { IconComponent } from '@examdojo/core/icon';
import { ButtonComponent } from '@examdojo/core/button';
import { IconButtonComponent } from '@examdojo/core/icon-button';
import { IonContent, IonFooter, IonHeader, IonModal, IonTitle, IonToolbar } from '@ionic/angular/standalone';
import { TranslocoPipe } from '@jsverse/transloco';
import { ModalCustomEvent } from '@ionic/core/components';
import { combineLatest, map, of, startWith, switchMap } from 'rxjs';
import { ExternalFilterType } from '../list-view.model';
import { ListViewCheckboxFilterComponent } from './list-view-checkbox-filter.component';
import {
  CheckboxExternalFilterColumn,
  ExternalFilterValue,
  RangeExternalFilterColumn,
} from './list-view-external-filtering.model';
import { ListViewExternalFilteringService } from './list-view-external-filtering.service';
import { ListViewRangeFilterComponent } from './list-view-range-filter.component';

@Component({
  selector: 'dojo-list-view-external-filtering',
  template: `
    <div class="button-wrap relative w-full md:w-auto">
      <dojo-button [fill]="'outline'" (click)="openDialog()" class="w-full md:w-auto">
        <dojo-icon [icon]="'sliders'" class="mr-2 text-neutral-400"></dojo-icon>
        <span>{{ 'assessment.popup.filters' | transloco }}</span>
      </dojo-button>
      @if (activeFiltersCount()) {
        <div
          class="border-neutral-0 text-neutral-0 aligner absolute right-[-7px] top-[-5px] h-6 min-w-6 rounded-full border-[2px] bg-sky-500 text-sm font-semibold"
        >
          {{ activeFiltersCount() }}
        </div>
      }
    </div>

    <ion-modal
      [isOpen]="isModalOpen()"
      (didDismiss)="closeDialog($event)"
      class="filtering-dialog"
      [showBackdrop]="true"
      [backdropDismiss]="true"
    >
      <ng-template>
        <ion-header [translucent]="true" class="mobile-header">
          <ion-toolbar class="toolbar-with-closing-button !px-2 md:!px-4">
            <div slot="start" class="flex">
              <dojo-icon-button (click)="closeDialog(true)" [icon]="'times'"></dojo-icon-button>
            </div>
            <div slot="end" class="w-8"></div>
            <ion-title class="common-ion-title">{{ 'assessment.popup.filters' | transloco }}</ion-title>
          </ion-toolbar>
        </ion-header>
        <ion-content>
          <div class="flex flex-col px-4 pb-4 md:px-6">
            <form [formGroup]="form" class="w-full">
              @if (state.hasFilters) {
                <div @appearDisappearWithHeight>
                  <div class="test border-b border-neutral-200 py-6">
                    <h4 class="text-md text-primary mb-4 font-semibold">
                      {{ 'assessment.popup.selected' | transloco }}
                    </h4>
                    <div class="flex flex-wrap gap-3">
                      @for (filterColumn of filterColumns(); track $index) {
                        @if (filterColumn.filter === ExternalFilterType.Checkbox) {
                          @if (filterColumn.formCtrl.value) {
                            @for (value of filterColumn.formCtrl.value; track value) {
                              <div class="selected-values">
                                <span class="selected-values-text">{{ value }}</span>
                                <button
                                  class="selected-values-button"
                                  (click)="removeCheckboxValue(filterColumn.colId, value)"
                                >
                                  <dojo-icon [icon]="'times'" class="text-sky-500"></dojo-icon>
                                </button>
                              </div>
                            }
                          }
                        }

                        @if (filterColumn.filter === ExternalFilterType.Range) {
                          @if (filterColumn.formCtrl.value) {
                            <div class="selected-values">
                              <span class="selected-values-text"
                                >{{ filterColumn.label }} : {{ filterColumn.formCtrl.value[0] }} -
                                {{ filterColumn.formCtrl.value[1] }}</span
                              >
                              <button class="selected-values-button" (click)="removeRangeValue(filterColumn.colId)">
                                <dojo-icon [icon]="'times'" class="text-sky-500"></dojo-icon>
                              </button>
                            </div>
                          }
                        }
                      }
                    </div>
                  </div>
                </div>
              }

              @for (filterColumn of filterColumns(); track $index) {
                @if (filterColumn.filter === ExternalFilterType.Checkbox) {
                  <div class="py-6">
                    <dojo-list-view-checkbox-filter
                      [formCtrl]="filterColumn.formCtrl"
                      [label]="filterColumn.label"
                      [options]="filterColumn.options ?? []"
                    />
                  </div>
                }

                @if (filterColumn.filter === ExternalFilterType.Range) {
                  <div class="border-t border-neutral-200 py-6">
                    <dojo-list-view-range-filter
                      [formCtrl]="filterColumn.formCtrl"
                      [label]="filterColumn.label"
                      [subText]="filterColumn.subText"
                      [options]="filterColumn.options ?? []"
                      [step]="filterColumn.step"
                    />
                  </div>
                }
              }
            </form>
          </div>
        </ion-content>
        <ion-footer
          class="shadow-subtle safe-area-bottom bg-neutral-0 flex flex-col border-t border-solid border-neutral-200"
        >
          <div class="flex justify-between px-4 py-4 md:px-6">
            <dojo-button class="w-auto" (click)="resetFilter()" [fill]="'outline'">
              {{ 'assessment.popup.clear_all' | transloco }}
            </dojo-button>
            <dojo-button (click)="confirm()">{{ 'assessment.popup.apply_filters' | transloco }}</dojo-button>
          </div>
        </ion-footer>
      </ng-template>
    </ion-modal>
  `,
  styleUrl: 'list-view-external-filtering.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ListViewCheckboxFilterComponent,
    ListViewRangeFilterComponent,
    ReactiveFormsModule,
    IonHeader,
    IconButtonComponent,
    IonToolbar,
    IonTitle,
    ButtonComponent,
    IonModal,
    IconComponent,
    IonFooter,
    IonContent,
    TranslocoPipe,
  ],
  animations: [getAnimationAppearDisappearWithHeight()],
  providers: [ListViewExternalFilteringService],
})
export class ListViewExternalFilteringComponent {
  constructor(private readonly listViewExternalFilteringService: ListViewExternalFilteringService) {}

  readonly ExternalFilterType = ExternalFilterType;

  readonly form = this.listViewExternalFilteringService.formGroup;

  readonly isModalOpen = signal(false);

  readonly filterColumns = this.listViewExternalFilteringService.filterColumns;

  readonly activeFiltersCount = signal<number>(0);

  readonly temporaryResetFormValue = signal<Record<string, ExternalFilterValue>>({});

  readonly state = connectState({
    hasFilters: toObservable(this.filterColumns).pipe(
      switchMap((filterColumns) => {
        return combineLatest(
          filterColumns.map((filterColumn) => {
            if (filterColumn.filter === ExternalFilterType.Checkbox) {
              return filterColumn.formCtrl.valueChanges.pipe(
                startWith(filterColumn.formCtrl.value),
                map((value) => Array.isArray(value) && value.length > 0),
              );
            }
            if (filterColumn.filter === ExternalFilterType.Range) {
              return filterColumn.formCtrl.valueChanges.pipe(
                startWith(filterColumn.formCtrl.value),
                map((value) => value !== null),
              );
            }

            return of(false);
          }),
        );
      }),
      map((values) => values.some((value) => value)),
    ),
  });

  removeCheckboxValue(colId: string, value: string) {
    const filterColumns = this.filterColumns();

    const filterColumn = filterColumns.find((column) => column.colId === colId);

    if (filterColumn) {
      const formCtrl = (filterColumn as CheckboxExternalFilterColumn).formCtrl;

      const valueArray = formCtrl.value as string[];

      const newValueArray = valueArray.filter((val) => val !== value);

      formCtrl.setValue(newValueArray);
    }
  }

  removeRangeValue(colId: string) {
    const filterColumns = this.filterColumns();

    const filterColumn = filterColumns.find((column) => column.colId === colId);

    if (filterColumn) {
      const formCtrl = (filterColumn as RangeExternalFilterColumn).formCtrl;

      formCtrl.setValue(null);
    }
  }

  resetFilter() {
    this.listViewExternalFilteringService.resetFilter();
  }

  closeDialog(event: true | ModalCustomEvent) {
    this.setModalOpen(false);

    if (event === true || event.detail.role === 'backdrop') {
      this.listViewExternalFilteringService.resetFilter(this.temporaryResetFormValue());
    }
  }

  openDialog() {
    this.temporaryResetFormValue.set(this.form.value);
    this.setModalOpen(true);
  }

  confirm() {
    this.listViewExternalFilteringService.applyFilter();
    this.setModalOpen(false);
    this.setFilterValuesCount();
  }

  private setModalOpen(value: boolean) {
    this.isModalOpen.set(value);
  }

  private setFilterValuesCount() {
    this.activeFiltersCount.set(
      this.filterColumns().reduce((acc, filterColumn) => {
        if (
          filterColumn.filter === ExternalFilterType.Checkbox &&
          Array.isArray(filterColumn.formCtrl.value) &&
          !!filterColumn.formCtrl.value.length
        ) {
          return acc + filterColumn.formCtrl.value.length;
        }
        if (filterColumn.filter === ExternalFilterType.Range) {
          return acc + (filterColumn.formCtrl.value ? 1 : 0);
        }
        return acc;
      }, 0),
    );
  }
}
